import { collection, doc, getDoc, getDocs } from "firebase/firestore";
import { AccountSchema, AccountView, nameofAccountSchemaCollection } from "../Types/AccountSchema";
import { auth, db, functions } from "../../firebase/firebase";
import { httpsCallable } from "firebase/functions";
import moment from "moment-timezone";
import Stripe from 'stripe';
import { nameofUserProfileSchemaCollection, UserProfileView } from "../Types/UserProfileSchema";
import { nameofStudentDetailSchemaCollection, StudentDetailSchema, StudentDetailView } from "../Types/StudentDetailSchema";


export async function getAccount(): Promise<AccountView | undefined> {
    if (auth.currentUser == null) {
        return undefined;
    }
    const currentUser = auth.currentUser;

    const docRef = doc(db, nameofAccountSchemaCollection, currentUser.uid);

    const account = await getDoc(docRef);
    if (account.exists()) {
        return {
            Id: account.id,
            FirstName: account.data().FirstName,
            MiddleName: account.data().MiddleName,
            LastName: account.data().LastName,
            PhoneNumber: account.data().PhoneNumber,
            Email: account.data().Email,
            DOB: account.data().DOB,
            SchoolID: account.data().SchoolID,
        } as AccountView;
    }
    return undefined;
}

export async function getUserProfile(userId: string): Promise<UserProfileView | undefined> {
    if (auth.currentUser == null) {
        return undefined;
    }
    

    const docRef = doc(db, nameofUserProfileSchemaCollection, userId);

    const userProfile = await getDoc(docRef);
    if (userProfile.exists()) {
        return {
            Id: userProfile.id,
            FirstName: userProfile.data().FirstName,
            MiddleName: userProfile.data().MiddleName,
            LastName: userProfile.data().LastName,
        } as UserProfileView;
    }
    return undefined;
}

export async function getAllUserProfiles(): Promise<UserProfileView[]> {
    
    if (auth.currentUser == null) {
        return [];
    }

    const userProfiles: UserProfileView[] = [];

    const docRef = collection(db, nameofUserProfileSchemaCollection);

    const userProfile = await getDocs(docRef);

    userProfile.forEach((doc) => {
        userProfiles.push({
            Id: doc.id,
            FirstName: doc.data().FirstName,
            MiddleName: doc.data().MiddleName,
            LastName: doc.data().LastName,
        } as UserProfileView);
    });
    return userProfiles;
}

export async function getAccountById(userId: string): Promise<AccountView | undefined> {
    const docRef = doc(db, nameofAccountSchemaCollection, userId);

    const account = await getDoc(docRef);
    if (account.exists()) {
        return {
            Id: account.id,
            FirstName: account.data().FirstName,
            MiddleName: account.data().MiddleName,
            LastName: account.data().LastName,
            PhoneNumber: account.data().PhoneNumber,
            Email: account.data().Email,
            DOB: account.data().DOB,
            SchoolID: account.data().SchoolID,
        } as AccountView;
    }
    return undefined;
}

export async function CreateUserAccount(newAccount: AccountSchema): Promise<void> {
    if (auth.currentUser === null) {
        return;
    }

    const currentUser = auth.currentUser;

    const createUserAccountFunction = httpsCallable(functions, "CreateUserAccount");

    const account: AccountSchema = {
        FirstName: newAccount.FirstName,
        MiddleName: newAccount.MiddleName,
        LastName: newAccount.LastName,
        PhoneNumber: newAccount.PhoneNumber,
        Email: currentUser?.email ?? "",
        DOB: newAccount.DOB,
        SchoolID: newAccount.SchoolID,
        TimeZone: moment.tz.guess(),
    };

    await createUserAccountFunction(
        {
            ...account
        }
    ).catch((error) => {
        throw new Error(error);
    });
}

export async function CreateTutorAccount(newAccount: AccountSchema): Promise<void> {
    if (auth.currentUser == null) {
        return;
    }

    const currentUser = auth.currentUser;

    const createTutorAccountFunction = httpsCallable(functions, "CreateTutorAccount");

    const account: AccountSchema = {
        FirstName: newAccount.FirstName,
        MiddleName: newAccount.MiddleName,
        LastName: newAccount.LastName,
        PhoneNumber: newAccount.PhoneNumber,
        Email: currentUser?.email ?? "",
        DOB: newAccount.DOB,
        SchoolID: newAccount.SchoolID,
        TimeZone: moment.tz.guess(),
    };

    await createTutorAccountFunction(
        {
            ...account
        }
    ).catch((error) => {
        return;
    });
}

export async function updateAccountSchoolID(SchoolID: string): Promise<void> {
    const updateAccountSchoolIDFunction = httpsCallable(functions, "updateAccountSchoolID");

    await updateAccountSchoolIDFunction(
        {
            SchoolID: SchoolID
        }
    ).catch((error) => {
        return;
    });

    return;
}

export async function getStudentDetail(): Promise<StudentDetailView | undefined> {
    if (auth.currentUser == null) {
        return undefined;
    }
    const currentUser = auth.currentUser;

    const docRef = doc(db, nameofStudentDetailSchemaCollection, currentUser.uid);

    const userDetail = await getDoc(docRef);
    if (userDetail.exists()) {
        return {
            Id: userDetail.id,
            StripeCustomerId: userDetail.data().StripeCustomerId,
            PreferedContactEmail: userDetail.data().PreferedContactEmail,
            PreferedContactPhone: userDetail.data().PreferedContactPhone,
            UserGuideCompleted: userDetail.data().UserGuideCompleted,
            StudentTOS: userDetail.data().StudentTOS,
        } as StudentDetailView;
    }
    return undefined;
}

export async function readUserGuide(): Promise<void> {
    if (auth.currentUser == null) {
        return;
    }

    const readUserGuideFunction = httpsCallable(functions, "readUserGuide");

    await readUserGuideFunction().catch((error) => {
        return;
    });

    return;
}

export async function getStripeAccount(): Promise<Stripe.Customer | undefined> {
    if (auth.currentUser == null) {
        return undefined;
    }

    const getStripeAccountFunction = httpsCallable(functions, "getStripeAccount");

    const stripeAccount = await getStripeAccountFunction();

    if (stripeAccount.data === null) {
        return undefined;
    }

    return stripeAccount.data as Stripe.Customer;
}

export async function updateStudentDetail(updatedStudentDetail: StudentDetailSchema): Promise<void> {
    if (auth.currentUser == null) {
        return;
    }

    const updateStudentDetailFunction = httpsCallable(functions, "updateStudentDetail");

    await updateStudentDetailFunction(
        {
            PreferedContactEmail: updatedStudentDetail.PreferedContactEmail,
            PreferedContactPhone: updatedStudentDetail.PreferedContactPhone,
        }
    )
    
    return;
}